/* eslint-disable @tencent/i18next/no-literal-string */
import { TextAnalyzer } from '@src/slide/application/service/slide-rating-and-improve/text/text-analyzer';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import {
    AnalyzerResultType,
    IElementCallback,
    IElementNode,
    ISlideCallback,
    ISlideNode,
    ITextBlockParentBoxNode,
    ITextElementBoxCallback,
    ITextElementBoxNode,
    ITextParagraphBoxNode,
} from './slide-content-analyzer.interface';
import { ColorAnalyzer } from '@src/slide/application/service/slide-rating-and-improve/color/color-analyzer';
import {
    IEnumerateCallback,
    ISlideContentEnumeratorService,
} from '@src/slide/application/service/slide-content-enumerator/slide-content-enumerator-service.interface';
import {
    ISlideContentLayoutAnalyzerService,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/slide-content-layout-analyzer-service.interface';
import {
    PageText,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/page-content';
import {
    ContentTree,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/content-tree';
import {
    IShowContentChangeOption,
    TitleAlignmentResult,
} from '@src/slide/application/service/slide-rating-and-improve/content-layout/result/title-alignment-result';
import i18n from '@i18next/i18n';
import {
    IPresentationCallback,
} from '@src/slide/application/service/slide-content-enumerator/enumerator-types.interface';

export interface AnalyzerOptions {
    forbidFontUseNumber: boolean;
}

export class SlideContentAnalyzer {
    public textAnalyzer: TextAnalyzer;
    public colorAnalyzer: ColorAnalyzer;
    public score: number;
    public titleAlignmentResult: TitleAlignmentResult;
    // public textStructureResult: PageContentTextStructureResult;

    public constructor(
        options: AnalyzerOptions,
        private uiDataService: IUiDataService,
        private slideContentEnumerator: ISlideContentEnumeratorService,
        private contentLayoutAnalyzer: ISlideContentLayoutAnalyzerService,
        showUniformFontUI: (onHandled: () => void, onCancel: () => void) => void,
        showContentChangeUI?: (options: IShowContentChangeOption) => void,
    ) {
        this.score = 100;
        this.textAnalyzer = new TextAnalyzer(options.forbidFontUseNumber, showUniformFontUI);
        this.colorAnalyzer = new ColorAnalyzer();
        // this.textStructureResult = new PageContentTextStructureResult(i18n.t('页面文本结构'), '页面文本');
        this.titleAlignmentResult = new TitleAlignmentResult(i18n.t('标题对齐检测'), i18n.t('建议不同页面间标题对齐'), showContentChangeUI);
    }

    public setShowContentChangeUI(showContentChangeUI: (options: IShowContentChangeOption) => void) {
        this.titleAlignmentResult.setShowContentChangeUI(showContentChangeUI);
    }

    public async analysis() {
        const onStartEnumerate = () => {
            this.textAnalyzer.resetWithPresentation(this.uiDataService.presentation);
            this.colorAnalyzer.resetWithPresentation(this.uiDataService.presentation);
            // this.textStructureResult.reset();
            this.titleAlignmentResult.reset();
        };
        const slideStartCallback: ISlideCallback = (slideNode: ISlideNode) => {
            this.colorAnalyzer.analysisNewSlide(slideNode.slide, slideNode.slideIndex);
        };
        const slideEndCallback: ISlideCallback = (slideNode: ISlideNode) => {
            this.textAnalyzer.onSlideEnd(slideNode);
        };
        const elementCallback: IElementCallback = (elementNode: IElementNode) => {
            this.colorAnalyzer.analysisElement(elementNode);
            this.textAnalyzer.analysisTextOverlapOfElement(elementNode);
        };

        const textBlockParentBoxCallback = (textBlockParentBoxNode: ITextBlockParentBoxNode) => {
            this.textAnalyzer.analysisTextDocument(textBlockParentBoxNode.textDocumentElement);
        };

        const textParagraphParentBoxCallback = (textParagraphBoxNode: ITextParagraphBoxNode) => {
            this.textAnalyzer.analysisLineSpacing(textParagraphBoxNode);
        };

        const textElementBoxCallback: ITextElementBoxCallback = (textElementBoxNode: ITextElementBoxNode) => {
            this.textAnalyzer.analysisFontSize(textElementBoxNode);
            this.textAnalyzer.analysisTextOverlapOfTextBox(textElementBoxNode);
            this.colorAnalyzer.analysisTextElementBox(textElementBoxNode);
        };

        const presentationEndCallback: IPresentationCallback = (): void => {
            this.textAnalyzer.onEnumeratePresentationEnd();
            this.colorAnalyzer.onPresentationEnd();
        };

        const enumerateCallbacks: IEnumerateCallback = {
            onStartEnumerate,
            slideStartCallback,
            slideEndCallback,
            elementCallback,
            textBlockParentBoxCallback,
            textParagraphParentBoxCallback,
            textElementBoxCallback,
            presentationEndCallback,
        };

        this.contentLayoutAnalyzer
            .analysisWithTextResultWhenEnumerateContent(
                this.uiDataService.presentation,
                enumerateCallbacks,
                (layoutResult: ContentTree[], textResult: PageText[], titleResult) => {
                    this.titleAlignmentResult.generateResult(
                        this.uiDataService.presentation,
                        titleResult,
                    );
                    // this.textStructureResult.setResultSource(textResult);
                    this.calculateScore();
                },
            );
        await this.slideContentEnumerator
            .enumerateWithCallback(enumerateCallbacks, { stopEnumerateHiddenElement: true });
    }

    public reset() {
        this.textAnalyzer.reset();
        this.colorAnalyzer.reset();
        this.titleAlignmentResult.reset();
    }

    // eslint-disable-next-line complexity
    public calculateScore(): void {
        let readabilityScore = 50;
        let beautifulScore = 50;
        if (this.textAnalyzer.lineSpacingResult.resultType === AnalyzerResultType.ERROR) {
            // 行间距
            readabilityScore -= 5;
            if (this.textAnalyzer.lineSpacingResult.resultInfo.length > 5) readabilityScore -= 5;
        }
        if (this.textAnalyzer.titleFontSizeResult.resultType === AnalyzerResultType.ERROR) {
            readabilityScore -= 2;
            if (this.textAnalyzer.titleFontSizeResult.resultInfo.length > 2) {
                readabilityScore -= 3;
            }
        }
        if (this.textAnalyzer.subTitleFontSizeResult.resultType === AnalyzerResultType.ERROR) {
            readabilityScore -= 2;
            if (this.textAnalyzer.subTitleFontSizeResult.resultInfo.length > 2) {
                readabilityScore -= 3;
            }
        }
        if (this.textAnalyzer.fontUseNumberResult?.resultType === AnalyzerResultType.ERROR) {
            // 字体数量
            beautifulScore -= 5;
            if (this.textAnalyzer.fontUseNumberResult.eaFontSet.size > 8) beautifulScore -= 5;
        }
        if (this.colorAnalyzer.textColorContrastResult.resultType === AnalyzerResultType.ERROR) {
            // 文字颜色和背景对比度低
            beautifulScore -= 5;
            if (this.colorAnalyzer.textColorContrastResult.resultInfo.length > 5) beautifulScore -= 5;
        }
        if (this.textAnalyzer.textOverlapResult.resultType === AnalyzerResultType.ERROR) {
            // 文字重叠
            beautifulScore -= 2;
            if (this.textAnalyzer.textOverlapResult.resultInfo.length > 10) beautifulScore -= 3;
        }
        if (this.titleAlignmentResult.resultType === AnalyzerResultType.ERROR) {
            // 标题不对齐
            readabilityScore -= 10;
        }
        this.score = readabilityScore + beautifulScore;
    }
}
